// The calendar's selected trips, as TripGroupMap legs.
//
// Nothing is resolved here, and that is the point: TripsCalendarSection builds
// groups out of `tripWithResolvedEndpoints` copies, and that helper writes the
// gazetteer-resolved route back into `fields` as a plain object (routeFromFields
// reads an object or a string). So `group.legs` are already exactly what the map
// wants -- a second resolve pass would be a second answer to a question already
// answered, and would need a placeIndex this hook has no business holding.
//
// Every leg carries its `groupKey`. TripGroupMap reads only id/from/to/fields,
// so the extra key rides along inert and comes back through onSelectLeg -- which
// is what makes both the color lookup and the navigation a map lookup rather
// than a re-derivation of the group key from a leg. Leg ids are database row
// ids, so TripGroupMap's `leg-${id}` layer ids stay unique across groups.
//
// DISPLAY COPIES, NEVER A WRITE PATH -- the same rule useEntryMapLegs states.
// The page's one write path (the handover on a bar or a line click) swaps back
// to the stored rows via groupWithStoredLegs before it navigates.

import { useMemo } from 'react';
import { hasPlacedWaypoints, routeFromTrip } from '../../fleet-shared/TripRoute.mjs';

// TripGroupMap builds a RouteLayer (a GeoJSON source plus line layers) per leg
// and a WaypointsLayer per marker, so a "Select all" over a few seasons of
// cruising is easily a thousand maplibre layers -- the same wall usePlaceMapLegs
// hit, at the same number. Duplicated rather than imported: a cross-page import
// for one integer buys nothing, and the two pages should be free to diverge.
export const MAP_LEG_LIMIT = 50;

export function useCalendarMapLegs(selectedGroups) {
  return useMemo(() => {
    // A trip with nothing placed can't be drawn and must not spend cap budget.
    const drawable = [];
    let noRouteGroupCount = 0;
    for (const group of selectedGroups || []) {
      const legs = (group.legs || []).filter((leg) => hasPlacedWaypoints(routeFromTrip(leg)));
      if (legs.length === 0) noRouteGroupCount += 1;
      else drawable.push({ group, legs });
    }

    // The cap drops whole TRIPS, newest first (selectedGroups is sorted desc),
    // because half a trip on the map reads as a data error rather than as a
    // limit. A trip is drawn completely or not at all, and the newest drawable
    // one is always drawn however long it is -- the cap is there to bound the
    // COMBINATION, and that one trip is already drawn uncapped on its own group
    // page, so refusing it here would be stricter than the page it came from
    // and would leave an empty map explaining itself only as a count.
    //
    // `break` rather than `continue`: the trips left off are then a suffix of
    // the list, which is what makes the notice sayable as "older trips".
    const kept = [];
    let budget = MAP_LEG_LIMIT;
    for (const entry of drawable) {
      if (kept.length > 0 && entry.legs.length > budget) break;
      budget -= entry.legs.length;
      kept.push(entry);
    }

    // Oldest first for drawing, matching usePlaceMapLegs and every other trip
    // map: color here comes from the trip, not the index, but draw order is
    // still z-order and time order is the one that reads.
    const mapLegs = kept
      .slice()
      .reverse()
      .flatMap(({ group, legs }) => legs.map((leg) => ({ ...leg, groupKey: group.key })));

    return {
      mapLegs,
      anyOnMap: mapLegs.length > 0,
      // What the card has to admit to: trips the cap took off the map, and
      // trips that have no route to draw in the first place.
      hiddenGroupCount: drawable.length - kept.length,
      noRouteGroupCount,
    };
  }, [selectedGroups]);
}

export default useCalendarMapLegs;
